'use client';

import React from 'react';
import { Button } from '@ui/components/ui/button';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';

interface JobDescriptionStepProps {
  onComplete: (data?: { skipped?: boolean }) => void;
  onSkip: () => void;
  onBack: () => void;
  isLoading: boolean;
}

/**
 * This step used to create a workflow and launch the guided product tour
 * (?tutorial=true). The tour is hidden behind PRODUCT_TOUR_ENABLED in
 * lib/constants.ts (see KB-289: the driver.js migration in PR #844 broke it
 * in production), so this step now just points the user at Products
 * instead of starting it. Restore the original CTA once the tour is fixed.
 */
export function JobDescriptionStep({ onComplete, onSkip, onBack, isLoading }: JobDescriptionStepProps) {
  const t = useTranslations('Onboarding.jobDescription');
  const tNav = useTranslations('Onboarding.navigation');
  const router = useRouter();

  const handleGetStarted = () => {
    onComplete();
    router.push('/products');
  };

  return (
    <div className="space-y-8 text-center">
      <div className="space-y-4">
        <h2 className="text-3xl font-bold">{t('title')}</h2>
        <p className="text-lg text-muted-foreground max-w-xl mx-auto">{t('subtitle')}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Button variant="ghost" onClick={onBack} disabled={isLoading} size="lg">
          {tNav('previous')}
        </Button>
        <Button variant="outline" onClick={onSkip} disabled={isLoading} size="lg">
          {t('skipStep')}
        </Button>
        <Button onClick={handleGetStarted} disabled={isLoading} size="lg">
          {tNav('finish')}
        </Button>
      </div>
    </div>
  );
}
